import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { RefreshCw, Plus, Send, MailCheck, MailX, Mail, AlertTriangle, CheckCircle2, Receipt, Eye } from 'lucide-react';
import { puntovService } from '../../../services/api';
import { useAuth } from '../../../hooks/useAuth';
import TablaDatos from './TablaDatos';
import FormularioCotizacionLibre from './FormularioCotizacionLibre';
import DialogoFactura from './DialogoFactura';
import { abrirPdf } from './abrirPdf';
import { fmtUsd, fmtPeriodo, fmtFechaHora, textoPlazoFactura } from './formato';

const ESTADOS_COTIZACION = {
  borrador: 'Borrador',
  en_proceso: 'En proceso',
  lista_confirmar: 'Lista para confirmar',
  confirmada: 'Confirmada',
  en_espera_pago: 'En espera de pago',
  pagada: 'Pagada',
  rechazada: 'Rechazada'
};

const estadoCorreo = (c) => (c.enviado_at && !c.error_envio ? 'enviado' : c.error_envio ? 'error' : 'sin_enviar');

const CHIP_FACTURA = {
  vencida: 'text-red-700 bg-red-50 border-red-200 hover:bg-red-100',
  pendiente: 'text-amber-700 bg-amber-50 border-amber-200 hover:bg-amber-100',
  enviada: 'text-green-700 bg-green-50 border-green-200 hover:bg-green-100'
};

const claveCotizacion = (c) => c.cotizacion_id;
const textoBusquedaCotizacion = (c) =>
  `${c.numero} ${c.cliente} ${c.empresa || ''} ${c.concepto || ''} ${c.periodo ? fmtPeriodo(c.periodo) : ''} ${c.correo_destino || ''}`;

const CotizacionesPuntoV = () => {
  const { user } = useAuth();
  const esAdmin = user?.rol === 'admin';

  const [cotizaciones, setCotizaciones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [aviso, setAviso] = useState(null);
  const [reenviando, setReenviando] = useState(null);
  const [mostrarNueva, setMostrarNueva] = useState(false);
  const [solicitudes, setSolicitudes] = useState({});
  const [facturaAbierta, setFacturaAbierta] = useState(null);

  const cargar = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [res, sol] = await Promise.all([
        puntovService.listarCotizaciones(),
        puntovService.listarSolicitudesFactura().catch(() => ({ data: [] }))
      ]);
      setCotizaciones(res.data || []);
      setSolicitudes(Object.fromEntries((sol.data || []).map(x => [x.id, x])));
    } catch (err) {
      setError(err.message || 'Error al cargar las cotizaciones');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const reenviar = useCallback(async (c) => {
    setReenviando(c.cotizacion_id);
    setAviso(null);
    try {
      const res = c.tipo === 'mensualidad'
        ? await puntovService.reenviarMensualidad(c.billing_invoice_id)
        : await puntovService.reenviarCotizacionLibre(c.cotizacion_id);
      setAviso({ tipo: 'exito', texto: `${c.numero}: ${res.message}` });
    } catch (err) {
      setAviso({ tipo: 'error', texto: `${c.numero}: ${err.message || 'no se pudo reenviar'}` });
    } finally {
      setReenviando(null);
      cargar();
    }
  }, [cargar]);

  const verCotizacion = useCallback((c) => {
    setAviso(null);
    abrirPdf(() => puntovService.verCotizacionPdf(c.cotizacion_id))
      .catch(err => setAviso({ tipo: 'error', texto: `${c.numero}: ${err.message}` }));
  }, []);

  const alCrear = (r) => {
    setMostrarNueva(false);
    const envio = r.envio;
    setAviso(!envio
      ? { tipo: 'exito', texto: `Cotización ${r.numero} creada sin enviar.` }
      : envio.enviado
        ? { tipo: 'exito', texto: `Cotización ${r.numero} creada y enviada a ${envio.para}.` }
        : { tipo: 'error', texto: `Cotización ${r.numero} creada, pero el correo no salió: ${envio.error}` });
    cargar();
  };

  const columnas = useMemo(() => [
    {
      id: 'numero',
      titulo: 'Número',
      ordenable: true,
      valor: (c) => c.fecha_creacion,
      render: (c) => (
        <>
          <div className="font-medium text-gray-800 tabular-nums">{c.numero}</div>
          <div className="text-xs text-gray-400">{fmtFechaHora(c.fecha_creacion)}</div>
        </>
      )
    },
    {
      id: 'cliente',
      titulo: 'Cliente',
      ordenable: true,
      valor: (c) => c.cliente,
      render: (c) => (
        <>
          <div className="text-gray-700">{c.cliente}</div>
          {c.empresa && <div className="text-xs text-gray-400">{c.empresa.replace(/^esgamanc_/, '')}</div>}
        </>
      )
    },
    {
      id: 'tipo',
      titulo: 'Tipo',
      valor: (c) => c.tipo,
      filtro: [
        { valor: 'mensualidad', etiqueta: 'Mensualidad' },
        { valor: 'libre', etiqueta: 'Libre' }
      ],
      render: (c) => (c.tipo === 'mensualidad'
        ? (
          <>
            <div className="text-gray-700">Mensualidad</div>
            <div className="text-xs text-gray-400">{fmtPeriodo(c.periodo)}</div>
            {c.factura_situacion && (
              <button
                type="button"
                onClick={() => solicitudes[c.solicitud_factura_id] && setFacturaAbierta(solicitudes[c.solicitud_factura_id])}
                disabled={!esAdmin || !solicitudes[c.solicitud_factura_id]}
                title="El cliente solicitó la factura de esta mensualidad"
                className={`mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md border text-[11px] font-medium whitespace-nowrap disabled:cursor-default ${CHIP_FACTURA[c.factura_situacion]}`}
              >
                <Receipt size={11} />
                {c.factura_situacion === 'enviada' ? 'Factura enviada'
                  : c.factura_situacion === 'vencida' ? 'Factura vencida'
                    : `Factura: ${textoPlazoFactura(solicitudes[c.solicitud_factura_id] || { dias_restantes: 0 }).toLowerCase()}`}
              </button>
            )}
          </>
        )
        : <><div className="text-gray-700">Libre</div><div className="text-xs text-gray-400 max-w-[14rem] truncate" title={c.concepto}>{c.concepto}</div></>)
    },
    {
      id: 'monto',
      titulo: 'Monto',
      ordenable: true,
      alinear: 'right',
      valor: (c) => Number(c.subtotal),
      render: (c) => <span className="font-medium tabular-nums text-gray-800">{fmtUsd(c.subtotal)}</span>
    },
    {
      id: 'estado',
      titulo: 'Estado',
      ordenable: true,
      valor: (c) => ESTADOS_COTIZACION[c.estado] || c.estado,
      render: (c) => <span className="text-xs text-gray-600">{ESTADOS_COTIZACION[c.estado] || c.estado}</span>
    },
    {
      id: 'correo',
      titulo: 'Correo',
      valor: estadoCorreo,
      filtro: [
        { valor: 'enviado', etiqueta: 'Enviado' },
        { valor: 'error', etiqueta: 'Con error' },
        { valor: 'sin_enviar', etiqueta: 'Sin enviar' }
      ],
      render: (c) => {
        const estado = estadoCorreo(c);
        if (estado === 'enviado') {
          return (
            <div className="text-xs">
              <div className="flex items-center gap-1 text-green-700 font-medium"><MailCheck size={13} /> Enviado</div>
              <div className="text-gray-400 truncate max-w-[13rem]" title={c.correo_enviado_a}>{c.correo_enviado_a} · {fmtFechaHora(c.enviado_at)}</div>
            </div>
          );
        }
        if (estado === 'error') {
          return (
            <div className="text-xs">
              <div className="flex items-center gap-1 text-red-600 font-medium"><MailX size={13} /> No se envió</div>
              <div className="text-gray-500 max-w-[13rem] truncate" title={c.error_envio}>{c.error_envio}</div>
            </div>
          );
        }
        return <div className="flex items-center gap-1 text-xs text-gray-400"><Mail size={13} /> Sin enviar</div>;
      }
    },
    {
      id: 'acciones',
      titulo: '',
      alinear: 'right',
      valor: () => '',
      render: (c) => (
        <div className="flex items-center justify-end gap-1.5">
          <button
            type="button"
            onClick={() => verCotizacion(c)}
            title="Ver el PDF tal como le llega al cliente"
            className="inline-flex items-center gap-1 px-2 py-1 text-xs text-gray-600 border border-gray-200 rounded-lg hover:border-blue-300 hover:text-blue-700 whitespace-nowrap"
          >
            <Eye size={12} /> Ver
          </button>
          {esAdmin && (
            <button
              type="button"
              onClick={() => reenviar(c)}
              disabled={reenviando === c.cotizacion_id || ['pagada', 'rechazada'].includes(c.estado)}
              title={c.correo_destino ? `Enviar a ${c.correo_destino}` : 'El cliente no tiene correo'}
              className="inline-flex items-center gap-1 px-2 py-1 text-xs text-gray-600 border border-gray-200 rounded-lg hover:border-blue-300 hover:text-blue-700 disabled:opacity-40 whitespace-nowrap"
            >
              <Send size={12} className={reenviando === c.cotizacion_id ? 'animate-pulse' : ''} />
              {reenviando === c.cotizacion_id ? 'Enviando…' : estadoCorreo(c) === 'enviado' ? 'Reenviar' : 'Enviar'}
            </button>
          )}
        </div>
      )
    }
  ], [esAdmin, reenviando, reenviar, verCotizacion, solicitudes]);

  const cerrarFactura = useCallback(() => setFacturaAbierta(null), []);

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <p className="text-sm text-gray-500">
          Las mensualidades las genera Punto V el día de cobro; las libres se crean aquí.
        </p>
        <div className="flex gap-2">
          <button
            onClick={cargar}
            disabled={loading}
            className="px-3 py-2 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 flex items-center gap-2 text-sm disabled:opacity-60"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} /> Actualizar
          </button>
          {esAdmin && (
            <button
              onClick={() => { setMostrarNueva(true); setAviso(null); }}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 text-sm font-medium"
            >
              <Plus size={16} /> Nueva cotización
            </button>
          )}
        </div>
      </div>

      {aviso && (
        <div className={`rounded-xl p-4 text-sm border flex items-start gap-2 ${
          aviso.tipo === 'error' ? 'bg-red-50 border-red-200 text-red-700' : 'bg-green-50 border-green-200 text-green-700'
        }`}>
          {aviso.tipo === 'error' ? <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" /> : <CheckCircle2 size={16} className="mt-0.5 flex-shrink-0" />}
          <span>{aviso.texto}</span>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 text-sm flex items-start gap-2">
          <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" /> <span>{error}</span>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {loading && cotizaciones.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto" />
          </div>
        ) : (
          <TablaDatos
            columnas={columnas}
            filas={cotizaciones}
            clave={claveCotizacion}
            textoBusqueda={textoBusquedaCotizacion}
            placeholderBusqueda="Buscar número, cliente o concepto"
            vacio="Todavía no hay cotizaciones de Punto V"
          />
        )}
      </div>

      {facturaAbierta && <DialogoFactura solicitud={facturaAbierta} onClose={cerrarFactura} onCambio={cargar} />}

      {mostrarNueva && (
        <FormularioCotizacionLibre onClose={() => setMostrarNueva(false)} onCreada={alCrear} />
      )}
    </div>
  );
};

export default CotizacionesPuntoV;
